import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { CAPABILITIES, IMAGES } from "../data/site";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function Capabilities() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  }, [hash]);

  return (
    <div data-testid="page-capabilities">
      <PageHero
        eyebrow="Our Capabilities"
        title="Multi-capability service delivery, structured for contract environments."
        intro="Street Society organizes its work into broad capability categories so clients can scope, combine, and expand services without re-procuring. Each category is built on disciplined field operations and clear accountability."
        image="cleanLobby"
      />

      <section className="bg-white" data-testid="capabilities-list">
        <div className="container-x section-y">
          <ul className="grid grid-cols-1 lg:grid-cols-7 gap-3 mb-16">
            {CAPABILITIES.map((c, i) => (
              <li key={c.id}>
                <a
                  href={`#${c.id}`}
                  className="block px-4 py-3 border border-[#E2E8F0] text-xs text-[#4A5568] hover:border-[#0A1F35] hover:text-[#0A1F35] transition-colors h-full"
                  data-testid={`capability-anchor-${c.id}`}
                >
                  <span className="text-overline text-[#9BB7A2] block mb-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-medium leading-snug">{c.title}</span>
                </a>
              </li>
            ))}
          </ul>

          <div className="space-y-24">
            {CAPABILITIES.map((cap, idx) => {
              const reverse = idx % 2 === 1;
              return (
                <article
                  key={cap.id}
                  id={cap.id}
                  className="scroll-mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
                  data-testid={`capability-section-${cap.id}`}
                >
                  <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="relative overflow-hidden">
                      <img
                        src={IMAGES[cap.image] || IMAGES.fieldOps}
                        alt={cap.title}
                        className="w-full h-[26rem] object-cover"
                        loading="lazy"
                      />
                      <div className="absolute top-5 left-5 bg-[#0A1F35] text-white px-4 py-2 text-overline">
                        {String(idx + 1).padStart(2, "0")} · {cap.naics !== "—" ? `NAICS ${cap.naics}` : "Custom"}
                      </div>
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                    <p className="text-overline text-[#4B7055] mb-3">Capability {String(idx + 1).padStart(2, "0")}</p>
                    <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">{cap.title}</h2>
                    <p className="mt-5 text-[#4A5568] text-base sm:text-lg leading-relaxed">{cap.long}</p>

                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      {buildHighlights(cap.id).map((h) => (
                        <li key={h} className="flex items-start gap-2.5 text-sm text-[#1A2530]">
                          <CheckCircle2 size={16} className="text-[#4B7055] mt-0.5 shrink-0" strokeWidth={2} />
                          {h}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8">
                      <Link to="/contact" className="btn-base btn-outline-dark" data-testid={`capability-cta-${cap.id}`}>
                        Discuss this scope <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}

function buildHighlights(id) {
  const map = {
    "facility-support": [
      "Day-porter and recurring janitorial",
      "Site readiness & turnover",
      "Restroom & high-touch programs",
      "Quality control documentation",
    ],
    "grounds-exterior": [
      "Turf and seasonal care",
      "Irrigation coordination",
      "Hardscape and exterior upkeep",
      "Public grounds and campuses",
    ],
    "mechanical-trade": [
      "Plumbing repair coordination",
      "HVAC service planning",
      "Preventive maintenance support",
      "Documented service records",
    ],
    "commercial-equipment": [
      "Equipment sourcing & logistics",
      "Operational deployment",
      "Vendor and manufacturer liaison",
      "Asset readiness reporting",
    ],
    "furnishings-interior": [
      "Workspace and office outfitting",
      "Installation coordination",
      "Public space furnishings",
      "Procurement-aligned sourcing",
    ],
    "laundry-specialty": [
      "Linen rotation programs",
      "Drycleaning coordination",
      "Hospitality & healthcare support",
      "Specialty cleaning scopes",
    ],
    "custom-contract": [
      "Teaming partner support",
      "Subcontract execution",
      "Scoped task orders",
      "Adaptive service planning",
    ],
  };
  return map[id] || [];
}
